import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DisplaySong } from '@/types/displayHistory';
import { fetchYearSongs } from '@/utils/displaySongsUtils';
import { Music, Sparkles, TrendingUp, Calendar, Youtube } from 'lucide-react';
import SongCard from './SongCard';
import YouTubeEmbed from '@/components/ui/youtube-embed';

interface DisplaySongsSectionProps {
  displayYearId: string | null;
  songCount: number;
  displayName?: string;
}

const DisplaySongsSection: React.FC<DisplaySongsSectionProps> = ({
  displayYearId,
  songCount,
  displayName = 'this display'
}) => {
  const [songs, setSongs] = useState<DisplaySong[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideoSong, setSelectedVideoSong] = useState<DisplaySong | null>(null);
  const [activeTab, setActiveTab] = useState<string>('all');

  useEffect(() => {
    const loadSongs = async () => {
      if (!displayYearId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const fetchedSongs = await fetchYearSongs(displayYearId);
        setSongs(fetchedSongs);
      } catch (error) {
        console.error('Error loading songs:', error);
        setSongs([]);
      } finally {
        setLoading(false);
      }
    };

    loadSongs();
  }, [displayYearId]);

  if (!displayYearId || songCount === 0) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center text-muted-foreground">
            <Music className="h-5 w-5 mr-2 animate-pulse" />
            Loading songs...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (songs.length === 0) {
    return null;
  }

  // Categorize songs
  const newSongs = songs.filter(song => !song.reused_from);
  const songsWithVideos = songs.filter(song => song.video_url);
  const songsWithSequences = songs.filter(song => song.sequence);
  const topRatedSongs = songs
    .filter(song => song.average_rating && song.rating_count && song.rating_count >= 3)
    .sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0))
    .slice(0, 6);

  const handlePlayPreview = (song: DisplaySong) => {
    console.log('🎬 handlePlayPreview called with song:', song.title);
    console.log('🎬 Video URL:', song.video_url);
    setSelectedVideoSong(song);
    console.log('🎬 State updated, selectedVideoSong should render now');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Music className="h-6 w-6" />
                Featured Songs
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {songs.length} {songs.length === 1 ? 'song' : 'songs'} featured at {displayName} this season
              </p>
            </div>
            <Badge variant="secondary" className="text-lg px-4 py-1">
              {songs.length}
            </Badge>
          </div>
        </CardHeader>

        <CardContent>
          {/* Selected Video Player - shows above tabs when video is playing */}
          {(() => {
            console.log('🎥 Render check - selectedVideoSong:', selectedVideoSong?.title || 'null');
            console.log('🎥 Render check - video_url:', selectedVideoSong?.video_url || 'null');
            console.log('🎥 Should render Now Playing?', !!(selectedVideoSong && selectedVideoSong.video_url));
            return selectedVideoSong && selectedVideoSong.video_url;
          })() && (
            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 mb-6">
              <CardHeader>
                <CardTitle className="text-lg">
                  Now Playing: {selectedVideoSong.title}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  by {selectedVideoSong.artist}
                </p>
              </CardHeader>
              <CardContent>
                <YouTubeEmbed
                  url={selectedVideoSong.video_url}
                  title={`${selectedVideoSong.title} by ${selectedVideoSong.artist}`}
                  className="w-full"
                />
              </CardContent>
            </Card>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-6">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <Music className="h-4 w-4" />
                All Songs
              </TabsTrigger>

              {newSongs.length > 0 && (
                <TabsTrigger value="new" className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  New ({newSongs.length})
                </TabsTrigger>
              )}

              {topRatedSongs.length > 0 && (
                <TabsTrigger value="top-rated" className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Top Rated ({topRatedSongs.length})
                </TabsTrigger>
              )}

              {songsWithVideos.length > 0 && (
                <TabsTrigger value="videos" className="flex items-center gap-2">
                  <Youtube className="h-4 w-4" />
                  Videos ({songsWithVideos.length})
                </TabsTrigger>
              )}
            </TabsList>

            {/* All Songs Tab */}
            <TabsContent value="all" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {songs.map((song) => (
                  <SongCard
                    key={song.id}
                    song={song}
                    onPlayPreview={handlePlayPreview}
                    showSequenceLink={true}
                  />
                ))}
              </div>
            </TabsContent>

            {/* New Songs Tab */}
            {newSongs.length > 0 && (
              <TabsContent value="new" className="space-y-4">
                <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    These songs are brand new to the display this year!
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {newSongs.map((song) => (
                    <SongCard
                      key={song.id}
                      song={song}
                      onPlayPreview={handlePlayPreview}
                      showSequenceLink={true}
                    />
                  ))}
                </div>
              </TabsContent>
            )}

            {/* Top Rated Songs Tab */}
            {topRatedSongs.length > 0 && (
              <TabsContent value="top-rated" className="space-y-4">
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-yellow-600" />
                    Fan favorites based on visitor ratings and downloads
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {topRatedSongs.map((song) => (
                    <SongCard
                      key={song.id}
                      song={song}
                      onPlayPreview={handlePlayPreview}
                      showSequenceLink={true}
                    />
                  ))}
                </div>
              </TabsContent>
            )}

            {/* Videos Tab */}
            {songsWithVideos.length > 0 && (
              <TabsContent value="videos" className="space-y-6">
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Youtube className="h-4 w-4 text-blue-600" />
                    Watch these songs in action at the display!
                  </p>
                </div>

                {/* Video Song Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {songsWithVideos.map((song) => (
                    <SongCard
                      key={song.id}
                      song={song}
                      onPlayPreview={handlePlayPreview}
                      showSequenceLink={true}
                    />
                  ))}
                </div>
              </TabsContent>
            )}
          </Tabs>

          {/* Download Sequences CTA */}
          {songsWithSequences.length > 0 && (
            <div className="mt-6 p-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold flex items-center gap-2">
                    <Music className="h-5 w-5 text-primary" />
                    Sequences Available for Download
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {songsWithSequences.length} of these songs have downloadable light sequences
                  </p>
                </div>
                <Button
                  variant="default"
                  onClick={() => setActiveTab('all')}
                  className="whitespace-nowrap"
                >
                  Browse All
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DisplaySongsSection;
